/**
 * SEOHead — React 19 document metadata
 * Renders <title> and <meta> tags that React 19 hoists to <head> automatically.
 */
interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  schema?: object;
  imageSitemap?: boolean;
}

export default function SEOHead({
  title,
  description,
  keywords,
  ogImage = 'https://spartechrd.com/imgs/og-default.jpg',
  canonical,
  schema,
  imageSitemap = true,
}: SEOHeadProps) {
  const fullTitle = title.includes('SpartechRD') ? title : `${title} | SpartechRD`;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="SpartechRD" />
      <meta name="last-modified" content={new Date().toISOString().split('T')[0]} />

      {/* Image search optimization */}
      <meta name="image" content={ogImage} />
      <meta name="image:alt" content={title} />
      <meta name="image:title" content={fullTitle} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="es_DO" />
      <meta property="og:site_name" content="SpartechRD" />
      {canonical && <meta property="og:url" content={canonical} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />

      {/* Canonical */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Image Sitemap reference */}
      {imageSitemap && (
        <link rel="sitemap" type="application/xml" title="Image Sitemap" href="https://ddnfeg1pc742qyp5lg3c.helloreaddy.com/functions/v1/image-sitemap" />
      )}

      {/* Geo tags — Santo Domingo Este, RD */}
      <meta name="geo.region" content="DO-02" />
      <meta name="geo.placename" content="Santo Domingo Este, República Dominicana" />
      <meta name="geo.position" content="18.4861;-69.8775" />
      <meta name="ICBM" content="18.4861, -69.8775" />

      {/* JSON-LD Schema */}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </>
  );
}